export interface LoginResponseBody {
    success: boolean;
    token: string;
    type: UserType;
}

export interface MainResponseBody {
    _id: string;
    email: string;
}

export type DataName = 'status' | 'report';

export type LayerName = 'UserInfo' | 'PSDSettings' | 'CameraSettings';

export interface UserConfigUserInfo {
    ID: string;
    Email: string;
}

export type RangeType = 'AUTO' | 'MANUAL';

export interface UserConfigPSDSettings {
    L1Visibility: boolean;
    L2Visibility: boolean;
    RangeType: RangeType;
    PowerMin: number;
    PowerMax: number;
}

export interface SingleCameraSettings {
    Name: string;
    CameraPos: number[];
    TargetPos: number[];
}

export interface UserConfigCameraSettings {
    CurrentCamera: string;
    Cameras: SingleCameraSettings[];
}

export enum ActionType {
    LOAD_ME_CONFIG = 'LOAD_ME_CONFIG',
    CHANGE_LAYER_FIELD = 'CHANGE_LAYER_FIELD',
    UDPATE_LAYER_DATA = 'UDPATE_LAYER_DATA',
    REQUEST_USER_CONFIG_STATE = 'REQUEST_USER_CONFIG_STATE',
    REQUEST_USER_LIST = 'REQUEST_USER_LIST',
    REQUEST_CREATE_USER = 'REQUEST_CREATE_USER',
    REQUEST_UPDATE_USER = 'REQUEST_UPDATE_USER',
    REQUEST_DELETE_USER = 'REQUEST_DELETE_USER',
    REQUEST_UPDATE_USER_PASSWORD = 'REQUEST_UPDATE_USER_PASSWORD',
    CLEAR_USER_EDIT_CACHE = 'CLEAR_USER_EDIT_CACHE',
    ACTIVITY_STATUS_REQUEST= 'ACTIVITY_STATUS_REQUEST',
    ACTIVITY_STATUS_SUCCESS= 'ACTIVITY_STATUS_SUCCESS',
    ACTIVITY_STATUS_FAILURE= 'ACTIVITY_STATUS_FAILURE',
    ACTIVITY_STATUS_REQUEST_ALL = 'ACTIVITY_STATUS_REQUEST_ALL',
    ACTIVITY_STATUS_SUCCESS_ALL = 'ACTIVITY_STATUS_SUCCESS_ALL',
    ACTIVITY_STATUS_FAILURE_ALL = 'ACTIVITY_STATUS_FAILURE_ALL',
    ACTIVITY_SPENT_TIME_REQUEST= 'ACTIVITY_SPENT_TIME_REQUEST',
    ACTIVITY_SPENT_TIME_SUCCESS= 'ACTIVITY_SPENT_TIME_SUCCESS',
    ACTIVITY_SPENT_TIME_FAILURE= 'ACTIVITY_SPENT_TIME_FAILURE',
}

export interface ActionChangeLayerField {
    type: ActionType.CHANGE_LAYER_FIELD;
    payload: {
        layerName: LayerName;
        fieldKey: string;
        fieldValue: any;
    };
}

export interface ActionUpdateLayerData {
    type: ActionType.UDPATE_LAYER_DATA;
    payload: {
        dataName: DataName;
        data: SensorNodeStatus | EventReport;
    };
}

export interface BaseAction {
    type: ActionType;
    payload: any;
}

export interface ActionLoadMainConfig {
    type: ActionType.LOAD_ME_CONFIG;
    payload: MainConfigResponseBody;
}

export interface ActionRequestState {
    type: ActionType;
    payload: HttpState;
}

export interface ActionRequestStateWithData<T> {
    type: ActionType;
    state: HttpState;
    payload: T;
}

export interface MainReportResponseBody {
    Report: string;
}

export interface MainConfigResponseBody {
    UserInfo: UserConfigUserInfo;
    PSDSettings: UserConfigPSDSettings;
    CameraSettings: UserConfigCameraSettings;
}

export type Band_Type = 'L1' | 'L2';

export interface ArrayDetection {
    Band: Band_Type;
    Avail: boolean;
    Cal: boolean;
    Jammer: boolean;
    Spoofer: boolean;
}

export interface DetectionStatus {
    Time: string;
    Array: ArrayDetection[];
}

export type TRK_Status = 'IDLE' | 'RESERVED' | 'ALLOCATED' | 'ACQUIRED';

export type TRK_Source = 'UNKNOWN' | 'GPS' | 'SPOOFER';

export type TRK_FPGA = 'NOTLOCKED' | 'LOCKED' | 'REACQUIRE';

export interface TrackingStatus {
    Time: string;
    PRN: number[];
    Status: TRK_Status[];
    Source: TRK_Source[];
    FPGA: TRK_FPGA[];
    Band: Band_Type[];
    LockTime: number[];
    SNR: number[];
    Azimuth: number[];
    Elevation: number[];
}

export type UBlox_FixStatus = 'NOFIX' | 'DRONLY' | '2DFIX' | '3DFIX' | 'GPSANDDR' | 'TIMEONLY' | 'RESERVED';

export type UBlox_JammingStatus = 'UNKNOWN' | 'OK' | 'WARNING' | 'CRITICAL';

export interface UBloxStatus {
    Time: string;
    Sat: number;
    FixStatus: UBlox_FixStatus;
    AGC: number;
    NoiseLevel: number;
    JammingStatus: UBlox_JammingStatus;
    JammingLevel: number;
    ClockBias: number;
    ClockDrift: number;
    TAcc: number;
    FAcc: number;
}

export interface ANCStatus {
    Time: string;
    Temperature: number;
}

export interface VisGNSS {
    Time: string;
    x: number[];
    y: number[];
    text: string[];
}

export interface ArrayConfig {
    Band: Band_Type;
    AntType: string;
    Position: number[];
}

export interface ArrayStatus {
    Time: string;
    Array: ArrayConfig[];
}

export interface ArrayCal {
    Band: Band_Type;
    Avail: boolean;
    Calibrated: boolean;
    Quality: number;
    Threshold: number;
    Azimuth: number;
}

export interface CalStatus {
    Time: string;
    Array: ArrayCal[];
}

export interface ArrayEigen {
    Band: Band_Type;
    Eigen: number[];
}

export interface EigenStatus {
    Time: string;
    Array: ArrayEigen[];
}

export interface ArrayPSD {
    Band: Band_Type;
    Freq: number[];
    Power: number[];
}

export interface PSDStatus {
    Time: string;
    Array: ArrayPSD[];
}

export interface BFSat {
    x: number[];
    y: number[];
    text: string[];
}

export interface BFIF {
    x: number[];
    y: number[];
}

export interface ArrayBF {
    Band: Band_Type;
    Beamformer: number[][];
    Sat: BFSat;
    Jammer: BFIF;
    Spoofer: BFIF;
}

export interface BFStatus {
    Time: string;
    Array: ArrayBF[];
}

export interface ArrayElementPower {
    Band: Band_Type;
    Power: number[];
}

export interface ElementPowerStatus {
    Time: string;
    Array: ArrayElementPower[];
}

export interface AOACesium {
    Azi: number;
    Ele: number;
    Range: number;
    Pos: number[];
}

export interface AOAStatus {
    Time: string;
    JammerL1: AOACesium[];
    JammerL2: AOACesium[];
    SpooferL1: AOACesium[];
}

export interface RelayInfo {
    Index: number;
    Name: string;
    Status: string;
}

export interface RelayStatus {
    Time: string;
    Name: string;
    Temperature: number;
    Relays: RelayInfo[];
}

export interface SNStatus {
    Name: string;
    Position: number[];
    Status: DetectionStatus;
    Tracking: TrackingStatus;
    UBlox: UBloxStatus;
    Ancillary: ANCStatus;
    VisGNSS: VisGNSS;
    Array: ArrayStatus;
    Calibration: CalStatus;
    Eigen: EigenStatus;
    PSD: PSDStatus;
    Beamformer: BFStatus;
    ElementPower: ElementPowerStatus;
    AOA: AOAStatus;
    Relay: RelayStatus;
}

export interface EventInfo {
    reportID: number;
    SN: string;
    Band: string;
    Type: string;
    StartTime: string;
    Period: number;
    MaxPower: number;
    NumbOfIF: number;
    OfflineReport: string;
    OnlineReport: string;
}

export interface EventReport {
    Time: string;
    SensorNodes: string[];
    L1: EventInfo[][];
    L2: EventInfo[][];
}

export interface SensorNodeStatus {
    Time: string;
    SensorNodes: string[];
    SensorNodeStatus: SNStatus[];
}

export interface GriffinData {
    Status: SensorNodeStatus;
    Reports: EventReport;
}

export enum HttpState {
    Idle = 'Idle',
    Progress = 'Progress',
    Success = 'Success',
    Failure = 'Failure'
}

export interface UserData {
    _id: string;
    email: string;
    usertype: UserType;
    enabled: boolean;
    alertL1: boolean;
    alertL2: boolean;
    minPeriod: number;
    maxPower: number;
}

export interface EmailTakenResponseBody {
    emailtaken: boolean;
}

export interface CreateUserRequestBody {
    email: string;
    password: string;
    usertype: string;
    enabled: boolean;
}

export interface UpdateUserRequestBody {
    usertype: string;
    enabled: boolean;
}

export type UserType = 'admin' | 'user' | 'moderator';

export interface MainInfo {
    id: string;
    email: string;
    usertype: UserType;
    iat: number;
}

export type ConfigActions = ActionLoadMainConfig | ActionRequestState | ActionChangeLayerField;
